import React, { Component } from 'react';
import Head from 'next/head';
import Script from 'next/script';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import SVG from '../svg';

import { 
  BaseWistiaButton,
  YellowWistiaButton,
  BaseWistiaWrapper,
  PlayWistiaButton,
  PlayWistiaButtonWrapper
} from './wistia-button-styled';


/**
 * Wistia Button for Wistia video Popovers.
 * For Wistia dev docs, see https://wistia.com/support/developers
 * ^ Warning, their dev docs are poorly organized.
 */
class WistiaButton extends Component {
  /**
   * Helper to extract the video id from a Wistia share link
   * Example format: https://glowforge.wistia.com/medias/xotlq75gkt
   * @param {String} shareLink Link from Wistia's share feature for a video
   */
  static getVideoId(shareLink) {
    // Regexp to extract the video id from a Wistia share url.\
    // The final group is the only one matched, and is in the [1] position.
    // Wistia provided this Regex
    const regexp = /https?:\/\/(?:.+)?(?:wistia\.com|wi\.st)\/(?:medias|embed)\/(.*)/;
    const groups = shareLink.match(regexp);
    return groups && groups[1];
  }

  constructor() {
    super();
    this.timeout = null;
    this.showPopover = this.showPopover.bind(this);
  }

  componentDidMount() {
    const { shareLink } = this.props;
    const videoId = WistiaButton.getVideoId(shareLink);
    if (videoId) {
      /* eslint-disable no-underscore-dangle */
      // Add video to Wistia queue, store ref to it's video object
      window._wq = window._wq || [];
      window._wq.push({
        id: videoId,
        async: true,
        popover: true,
        popoverContent: 'link',
        onReady: (videoRef) => {
          this.videoRef = videoRef;
        },
      });
      /* eslint-enable no-underscore-dangle */
    }
  }

  componentWillUnmount() {
    if (this.videoRef) {
      this.videoRef.popover.hide();
      setTimeout(() => this.videoRef.remove());
    }
    clearTimeout(this.timeout);
  }

  /**
   * After the video has loaded, we can interact with it via
   * Wistia's JS api
   */
  showPopover() {
    clearTimeout(this.timeout);

    const playIfReady = () => {
      if (this.videoRef) {
        this.videoRef.popover.show();
        setTimeout(() => this.videoRef.play());
      } else {
        this.timeout = setTimeout(() => playIfReady(), 50);
      }
    };

    playIfReady();
  }

  render() {
    const {
      buttonText,
      className,
      preload,
      shareLink,
      size,
      type,
    } = this.props;

    const isPlayButton = type === 'playButton';
    const buttonType = isPlayButton ? 'clearButton' : type;
    let icon = isPlayButton ? 'play-button' : 'play-circle';
    // Yellow type needs a black play-circle icon
    const isYellow = type === 'yellow';
    icon = isYellow ? 'play-circle-black' : icon;
    const iconComponent = (
      <SVG 
        core
        internalName={icon}
        id={icon}
        className='play-video-icon'
      />
    );
    const text = isPlayButton ? null : buttonText;

    const videoId = WistiaButton.getVideoId(shareLink);

    const sharedProps = {
      onClick: this.showPopover,
      'aria-label': `Play Button for video: ${buttonText}`,
      className: classnames('WistiaButton', className, { playButton: isPlayButton }),
    };

    const playCircleComponent = (
      <PlayWistiaButton
        {...sharedProps}>
        {iconComponent}
      </PlayWistiaButton>
    );

    const playButtonComponent = isYellow ? (
      <YellowWistiaButton
        {...sharedProps}
        icon={iconComponent}
        type={buttonType}
        size={size}
        variant='yellow'>
        {text}
      </YellowWistiaButton>
    ):(
      <BaseWistiaButton
        {...sharedProps}
        icon={iconComponent}
        type={buttonType}
        size={size}>
        {text}
      </BaseWistiaButton>
    );

    const Wrapper = isPlayButton ? PlayWistiaButtonWrapper : BaseWistiaWrapper;

    return (
      <>
        <Script src='https://fast.wistia.com/assets/external/E-v1.js' strategy='afterInteractive' />
        <Head>
          <style type='text/css'>{`
            /* Dont allow any scrolling when video is shown */
            .wistia_popover_mode {
              overflow: hidden;
            }

            /* prevent wistia popover_embed from overflowing onload. 
              this is a bug with Wistia's code. */
            .wistia_popover_embed,
            div[id^="wistia"][id$="_popover"] {
              max-width: 100%;
            }
          `}</style>
        </Head>
        <Wrapper className={classnames('WistiaButton-wrapper', { playButton: isPlayButton })}>
          {/* Wistia expects the below div to know to inject it's markup */}
          <div
            className={
              classnames(
                'wistia_embed',
                `wistia_async_${videoId}`,
                'popover=true',
                {
                  'preload=true': preload,
                },
          )}
          />
          { isPlayButton
            ? playCircleComponent
            : playButtonComponent
          }
        </Wrapper>
      </>
    );
  }
}

WistiaButton.propTypes = {
  buttonText: PropTypes.string,
  className: PropTypes.string,
  preload: PropTypes.bool,
  shareLink: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
  type: PropTypes.oneOf(['text', 'default', 'primary', 'secondary', 'yellow', 'playButton']),
};

WistiaButton.defaultProps = {
  buttonText: '',
  className: '',
  preload: false,
  shareLink: '',
  size: 'medium',
  type: 'default',
};

export default WistiaButton;
